


/* TODO: Split this component into smaller components. */
import { Component, Vue, Watch } from "vue-property-decorator";

import { Configuration, Subscriptions } from "App";

import { Recurrence } from "Shared/Models";

import {
    Subscription,
    Field,
    Invoice,
    CustomNotification,
    GlobalConfiguration
} from "App/Models";

import { Activated, Dictionary, Identifiable, Named } from "App/Contracts";

import { EventBus, $t } from "App/IOC";

import { RecordActions, RecordAction } from "Web/Contracts";

import SubscriptionForm from "./Form.vue";

import Record from "Web/Components/Record.vue";
import PopupWindow from "Web/Components/Popup/Window.vue"
import LoadingDots from "Web/Components/Common/LoadingDots.vue";

/* TVA française applicable aux abonnements : sert à dériver le TTC affiché à partir du HT. */
const VAT_RATE: number = 0.2;

@Component({
    components: {
        SubscriptionForm,
        Record,
        PopupWindow,
        LoadingDots
    }
})
export default class SingleSubscriptionView extends Vue {
    private record?: Subscription = {} as Subscription;

    private configuration?: GlobalConfiguration;

    private fields: Dictionary<Field> = {};

    private firstStepCancellation: boolean = false;
    private secondStepCancellation: boolean = false;
    private editing: boolean = false;
    private loading: boolean = true;
    private slectedAction: RecordAction = {} as RecordAction;
    private confirmCancellationAction: Identifiable & Named & Activated = {
        Id: "confirmCancellation",
        Name: $t( "SUBSCRIPTION_confirmCancellation" ),
        Active: true
    };

    private actions: RecordActions<Subscription> = [
        [
            {
                Id: "RedirectToStripe",
                Title: $t( "SUBSCRIPTION_actionStripeRedirection" ),
                Icon: "link"
            },

            {
                Id: "Unsubscribe",
                Title: $t( "SUBSCRIPTION_actionUnsubscribe" ),
                Icon: "delete_forever",
                Enabled: this.isActionEnabled
            },

            {
                Id: "Cancel",
                Title: $t( "SUBSCRIPTION_actionCancel" ),
                Icon: "delete_forever",
                Enabled: this.isActionEnabled
            },

            {
                Id: "ChangeRecurrence",
                Title: $t( "SUBSCRIPTION_actionChangeRecurrence" ),
                Icon: "autorenew",
                Enabled: this.isActionEnabled
            }
        ]
    ]

    private recurrenceConfirmation: boolean = false;
    private recurrenceSecondStep: boolean = false;

    /* Pendant la requête : CTA conservé (orange, largeur), LoadingDots défilant dessous. */
    private actionLoading: boolean = false;

    private async created() {
        this.record = undefined;
        await this.setup();
    }

    private async setup() {
        this.fields = await Subscriptions.Fields.Get();

        await this.getSubscription();

        /* Configuration globale : sert la période d'essai affichée dans l'en-tête. */
        const [ configuration ] = await Configuration.Get();
        this.configuration = configuration;
    }

    /* Reset data on Subscription change*/
    private resetData() {
        this.fields = {};
    }

    @Watch( "$route" )
    private async onRouteParametersChange( from: any, to: any ) {
        if( to.name !== "subscription" || from.params.id === to.params.id )
            return;

        this.resetData();
        await this.setup();
    }

    private async getSubscription() {
        const [ record, error ] = await Subscriptions.Get( Number( this.$route.params.id ) );

        if( error !== undefined || record === undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.record = record as Subscription;
    }

    private get subscriptionPlans(): string {
        return this.record !== undefined ? Subscriptions.GenerateTitle( this.record ) : "";
    }

    /* En-tête : nom du plan en titre, prix mensuel / annuel en dessous. */
    /* Nom(s) de plan bruts, suffixe "(Essai)" compris. */
    private get rawPlanTitle(): string {
        if( this.record === undefined )
            return "";

        const names: string[] = [];

        if( this.record.Plans !== undefined )
            for( const plan of this.record.Plans )
                if( plan.Name !== undefined && plan.Name !== ""
                    && names.indexOf( plan.Name ) === -1 )
                    names.push( plan.Name );

        /* Abonnement résilié : Plans peut être vide, on retombe sur les lignes (Plan puis Produit)
           pour ne pas perdre le nom de l'abonnement dans l'en-tête. */
        if( names.length === 0 && this.record.Lines !== undefined )
            for( const line of this.record.Lines ) {
                const fallback = ( line.Plan !== undefined && line.Plan.Name )
                    ? line.Plan.Name
                    : ( line.Product !== undefined ? line.Product.Name : "" );

                if( fallback && names.indexOf( fallback ) === -1 )
                    names.push( fallback );
            }

        return names.join( ", " );
    }

    private get planTitle(): string {
        let title = this.rawPlanTitle;

        /* En essai, le suffixe "(Essai)" du nom de plan est redondant avec le badge
           dédié affiché dans l'en-tête : on le retire du titre. */
        if( this.isTrial )
            title = title.replace( /\s*\(essai\)\s*/gi, " " )
                .replace( /\s+,/g, "," )
                .replace( /\s{2,}/g, " " )
                .trim();

        return title;
    }

    /* Abonnement en essai : badge dédié dans l'en-tête. Déclenché soit par le statut DB
       (UseCase IsTrial), soit par le suffixe "(Essai)" présent dans le nom de plan
       (plans de catalogue d'essai qui vivent en statut Offer/Active). */
    private get isTrial(): boolean {
        if( this.record === undefined )
            return false;

        return Subscriptions.IsTrial( this.record )
            || /\(essai\)/i.test( this.rawPlanTitle );
    }

    /* Abonnement résilié (résiliation programmée ou déjà annulé) : affiche un badge dédié. */
    private get isCanceled(): boolean {
        return this.record !== undefined && Subscriptions.IsCanceled( this.record );
    }

    /* En-tête : facture la plus récente (par date de création), pour afficher le dernier statut
       de paiement en badge à côté du plan. */
    private get latestInvoice(): Invoice | undefined {
        if( this.record === undefined || this.record.Invoices === undefined )
            return undefined;

        let latest: Invoice | undefined;

        for( const invoice of this.record.Invoices ) {
            if( invoice.CreatedAt === undefined )
                continue;

            if( latest === undefined
                || latest.CreatedAt === undefined
                || invoice.CreatedAt.ToTimestamp() > latest.CreatedAt.ToTimestamp() )
                latest = invoice;
        }

        return latest;
    }

    /* Statut Stripe brut de la dernière facture (Paid / Open / Uncollectible / Void), sert de
       modificateur de classe pour la couleur du badge. */
    private get lastPaymentStatus(): string {
        const invoice = this.latestInvoice;

        return invoice !== undefined && invoice.Status !== undefined ? invoice.Status : "";
    }

    /* Libellé traduit du dernier statut de paiement (mêmes libellés que l'onglet Facturation).
       Draft / Rejected -> vide : pas de badge affiché. */
    private get lastPaymentStatusLabel(): string {
        switch( this.lastPaymentStatus ) {
            case "Paid":
                return this.$t( "SUBSCRIPTION_paid" ).toString();

            case "Open":
                return this.$t( "SUBSCRIPTION_open" ).toString();

            case "Uncollectible":
                return this.$t( "SUBSCRIPTION_uncollectible" ).toString();

            case "Void":
                return this.$t( "SUBSCRIPTION_void" ).toString();

            default:
                return "";
        }
    }

    /* Extrait le montant numérique d'une ligne quel que soit le type renvoyé. */
    private lineAmount( line: any ): number {
        const subtotal = line.Subtotal;

        if( subtotal === undefined || subtotal === null )
            return 0;

        if( typeof subtotal === "number" )
            return subtotal;

        if( typeof subtotal.ToFixed === "function" )
            return Number( subtotal.ToFixed( 2 ) );

        return Number( subtotal ) || 0;
    }

    /* Somme des lignes récurrentes, exprimée dans le cycle de l'abonnement. */
    private get recurrentTotal(): number {
        if( this.record === undefined || this.record.Lines === undefined )
            return 0;

        let total = 0;

        for( const line of this.record.Lines ) {
            if( line.Price === undefined || line.Price.Recurrence === undefined )
                continue;

            total += this.lineAmount( line );
        }

        return total;
    }

    private get monthlyPrice(): number {
        return ( this.record !== undefined && this.record.Recurrence === "Yearly" )
            ? this.recurrentTotal / 12
            : this.recurrentTotal;
    }

    private get yearlyPrice(): number {
        return this.monthlyPrice * 12;
    }

    /* Prix TTC dérivés du HT en appliquant la TVA. */
    private get monthlyPriceTTC(): number {
        return this.monthlyPrice * ( 1 + VAT_RATE );
    }

    private get yearlyPriceTTC(): number {
        return this.yearlyPrice * ( 1 + VAT_RATE );
    }

    private formatAmount( value: number ): string {
        return value.toLocaleString( "fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    private get firstSubscriptionDate(): string {
        if( this.record === undefined || this.record.CreatedAt === undefined )
            return "";

        return this.record.CreatedAt.Format(
            this.$t( "GLOBAL_default_standard_date_format" ) as string
        );
    }

    /* En-tête : date du dernier changement effectué sur l'abonnement. */
    private get lastUpdatedDate(): string {
        const updatedAt = this.record === undefined
            ? undefined
            : ( this.record as any ).UpdatedAt;

        if( updatedAt === undefined )
            return "";

        return updatedAt.Format(
            this.$t( "GLOBAL_default_standard_date_format" ) as string
        );
    }

    /* En-tête : récurrence courante (Mensuel / Annuel) de l'abonnement. */
    private get recurrenceLabel(): string {
        if( this.record === undefined || this.record.Recurrence === undefined )
            return "";

        return this.record.Recurrence === "Yearly"
            ? this.$t( "SUBSCRIPTIONS_recurrenceYearly" ).toString()
            : this.$t( "SUBSCRIPTIONS_recurrenceMonthly" ).toString();
    }

    /* Période de facturation en cours : de (prochaine échéance − 1 cycle) à la prochaine échéance.
       Le début est dérivé car le payload n'expose que NextInvoiceDate. */
    private get currentPeriod(): string {
        if( this.record === undefined || this.record.NextInvoiceDate === undefined )
            return "";

        const format = this.$t( "GLOBAL_default_standard_date_format" ) as string;
        const unit = this.record.Recurrence === "Yearly" ? "year" : "month";

        /* Add() mute l'instance en place : copier avant pour ne pas écraser NextInvoiceDate. */
        const start = this.record.NextInvoiceDate.Copy().Add( -1, unit );

        return this.$t( "SUBSCRIPTION_currentPeriod", [
            start.Format( format ),
            this.record.NextInvoiceDate.Format( format )
        ] ).toString();
    }

    /* En-tête : bon de réduction appliqué (premier coupon de l'abonnement). */
    private get appliedCouponName(): string {
        if( this.record === undefined
            || this.record.Coupons === undefined
            || this.record.Coupons.length === 0 )
            return "";

        return this.record.Coupons[ 0 ].Name || "";
    }

    private goToCoupon(): void {
        if( this.record === undefined
            || this.record.Coupons === undefined
            || this.record.Coupons.length === 0 )
            return;

        this.$router.push( `/coupons/${ this.record.Coupons[ 0 ].Id }` );
    }

    /* En-tête : période d'essai (jours) issue de la configuration globale. */
    private get trialDays(): string {
        const configuration = this.configuration as any;

        if( configuration === undefined || configuration === null )
            return "";

        const trial = configuration.Trial;

        return ( trial !== undefined && trial !== null && trial !== "" )
            ? String( trial )
            : "";
    }

    private execute( action: RecordAction ): void {
        switch( action.Id ) {
            case "Cancel":
            case "Unsubscribe":
                this.firstStepCancellation = true;
                this.slectedAction = action;

                break;

            case "RedirectToStripe":
                this.redirectToStripeSubscription();
                break;

            case "ChangeRecurrence":
                this.recurrenceConfirmation = true;
                this.slectedAction = action;

                break;

            default:
                EventBus.Publish( "UnderContruction" );
                break;
        }
    }

    private redirectToStripeSubscription() {
        if( this.record === undefined )
            return;

        window.open( `https://dashboard.stripe.com/id/${this.record.ExternalId}` );
    }

    private async unsubscribe(): Promise<void> {
        if( this.record === undefined )
            return;

        this.confirmCancellationAction.Active = false;

        /* In case if the user chooses to unsubscribe from the subscription immediately we need to
           cancel this subscription, otherwise it will be canceled at the end of its period. */
        const error =
            await Subscriptions.Unsubscribe( this.record, this.slectedAction.Id === "Cancel" );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        EventBus.Publish( "Notification", CustomNotification.Success(
            this.$t( "SUBSCRIPTION_unsubscribeSuccessTitle" ).toString(),
            this.$t( "SUBSCRIPTION_unsubscribeSuccessMessage" ).toString()
        ));

        /* Remove the last cancellation popups after cancelling the subscription. */
        this.secondStepCancellation = false;
        this.confirmCancellationAction.Active = true;
    }

    private get targetRecurrence(): Recurrence {
        return ( this.record !== undefined && this.record.Recurrence === "Yearly" )
            ? "Monthly"
            : "Yearly";
    }

    /* Récurrence cible (mensuelle / annuelle) en gras, partagée par les modales. */
    private get recurrencePhraseBold(): string {
        const phrase = this.targetRecurrence === "Yearly"
            ? this.$t( "SUBSCRIPTION_recurrencePhraseYearly" ).toString()
            : this.$t( "SUBSCRIPTION_recurrencePhraseMonthly" ).toString();

        return `<strong class="c-subscription__recurrence-em">${phrase}</strong>`;
    }

    /* 1ère modale : message explicatif, récurrence cible en gras (injecté via v-html). */
    private get recurrenceChangeMessageHtml(): string {
        return this.$t( "SUBSCRIPTION_changeRecurrenceMessage",
            [ this.recurrencePhraseBold ] ).toString();
    }

    /* 2nde modale : confirmation du changement manuel, récurrence cible en gras (via v-html). */
    private get recurrenceConfirmMessageHtml(): string {
        return this.$t( "SUBSCRIPTION_changeRecurrenceConfirmMessage",
            [ this.recurrencePhraseBold ] ).toString();
    }

    /* Passe à la seconde confirmation (double validation) avant le changement de récurrence. */
    private showRecurrenceSecondStep(): void {
        this.recurrenceConfirmation = false;
        this.recurrenceSecondStep = true;
    }

    private async changeRecurrence(): Promise<void> {
        if( this.record === undefined || this.actionLoading )
            return;

        this.actionLoading = true;

        const error = await Subscriptions.ChangeRecurrence( this.record, this.targetRecurrence );

        if( error !== undefined ) {
            this.actionLoading = false;
            EventBus.Publish( "Error", error );
            return;
        }

        EventBus.Publish( "Notification", CustomNotification.Success(
            this.$t( "SUBSCRIPTION_changeRecurrenceSuccessTitle" ).toString(),
            this.$t( "SUBSCRIPTION_changeRecurrenceSuccessMessage" ).toString()
        ));

        this.actionLoading = false;
        this.recurrenceConfirmation = false;
        this.recurrenceSecondStep = false;

        await this.getSubscription();
    }

    private showConfirmationSteps(): void {
        this.firstStepCancellation = false;
        this.secondStepCancellation = true;
    }

    private isActionEnabled(): boolean {
        if( this.record === undefined )
            return false;

        return !Subscriptions.IsCanceled( this.record );
    }
}

